// Bidirectional conversion between Jira ADF (Atlassian Document Format) and TipTap JSON

import { formatDate } from '@platform/app-shell/frontend/utils/dateFormat';
import { bareJiraIssueLink } from './smartLink.js';

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph', content: [] }] };

// TipTap node types our editor schema supports
const KNOWN_TIPTAP_NODES = new Set([
  'doc', 'paragraph', 'heading', 'text', 'bulletList', 'orderedList', 'listItem',
  'codeBlock', 'blockquote', 'horizontalRule', 'image', 'hardBreak',
  'table', 'tableRow', 'tableCell', 'tableHeader', 'mention', 'inlineCard',
]);

// TipTap mark types our editor schema supports
const KNOWN_TIPTAP_MARKS = new Set([
  'bold', 'italic', 'underline', 'link', 'strike', 'code', 'textColor',
]);

// Node type mappings: ADF → TipTap
const ADF_TO_TIPTAP_NODES = {
  rule: 'horizontalRule',
};

// Node type mappings: TipTap → ADF
const TIPTAP_TO_ADF_NODES = {
  horizontalRule: 'rule',
};

// Mark type mappings: ADF → TipTap
const ADF_TO_TIPTAP_MARKS = {
  strong: 'bold',
  em: 'italic',
};

// Mark type mappings: TipTap → ADF
const TIPTAP_TO_ADF_MARKS = {
  bold: 'strong',
  italic: 'em',
};

function convertMarkAdfToTipTap(mark) {
  const type = ADF_TO_TIPTAP_MARKS[mark.type] || mark.type;

  // Strip unknown marks — TipTap rejects the entire doc if any mark type is unrecognized
  if (!KNOWN_TIPTAP_MARKS.has(type)) {
    console.log('[adfToTipTap] Stripping unknown mark type:', mark.type);
    return null;
  }

  const result = { type };

  if (mark.type === 'link' && mark.attrs) {
    result.attrs = { href: mark.attrs.href, target: '_blank' };
  } else if (mark.attrs) {
    result.attrs = { ...mark.attrs };
  }

  return result;
}

function convertMarkTipTapToAdf(mark) {
  const type = TIPTAP_TO_ADF_MARKS[mark.type] || mark.type;
  const result = { type };

  if (mark.type === 'link' && mark.attrs) {
    result.attrs = { href: mark.attrs.href };
  } else if (mark.attrs) {
    result.attrs = { ...mark.attrs };
  }

  return result;
}

function convertNodeAdfToTipTap(node) {
  if (!node) return node;

  // A plain link whose text is its own Jira /browse/KEY address (how Jira
  // kept many pasted links) loads as a smart link, like Jira's own cards.
  const jiraHref = bareJiraIssueLink(node);
  if (jiraHref) return { type: 'inlineCard', attrs: { url: jiraHref } };

  // ADF table → TipTap table
  if (node.type === 'table') {
    return {
      type: 'table',
      content: (node.content || []).flatMap(c => { const r = convertNodeAdfToTipTap(c); return !r ? [] : Array.isArray(r) ? r : [r]; }),
    };
  }

  if (node.type === 'tableRow') {
    return {
      type: 'tableRow',
      content: (node.content || []).flatMap(c => { const r = convertNodeAdfToTipTap(c); return !r ? [] : Array.isArray(r) ? r : [r]; }),
    };
  }

  if (node.type === 'tableHeader') {
    const attrs = {};
    if (node.attrs?.colspan) attrs.colspan = node.attrs.colspan;
    if (node.attrs?.rowspan) attrs.rowspan = node.attrs.rowspan;
    if (node.attrs?.colwidth) attrs.colwidth = node.attrs.colwidth;
    if (node.attrs?.background) attrs.background = node.attrs.background;
    return {
      type: 'tableHeader',
      attrs: Object.keys(attrs).length ? attrs : undefined,
      content: (node.content || []).flatMap(c => { const r = convertNodeAdfToTipTap(c); return !r ? [] : Array.isArray(r) ? r : [r]; }),
    };
  }

  if (node.type === 'tableCell') {
    const attrs = {};
    if (node.attrs?.colspan) attrs.colspan = node.attrs.colspan;
    if (node.attrs?.rowspan) attrs.rowspan = node.attrs.rowspan;
    if (node.attrs?.colwidth) attrs.colwidth = node.attrs.colwidth;
    if (node.attrs?.background) attrs.background = node.attrs.background;
    return {
      type: 'tableCell',
      attrs: Object.keys(attrs).length ? attrs : undefined,
      content: (node.content || []).flatMap(c => { const r = convertNodeAdfToTipTap(c); return !r ? [] : Array.isArray(r) ? r : [r]; }),
    };
  }

  // mediaSingle → image (for external media URLs or internal Jira attachments)
  if (node.type === 'mediaSingle') {
    const mediaChild = node.content?.[0];
    if (mediaChild?.type === 'media') {
      // Preserve width from mediaSingle attrs
      const msWidth = node.attrs?.width || null;
      if (mediaChild.attrs?.type === 'external' && mediaChild.attrs?.url) {
        return {
          type: 'image',
          attrs: { src: mediaChild.attrs.url, alt: mediaChild.attrs.alt || null, width: msWidth },
        };
      }
      // Internal Jira media (type: 'file') — try to render via attachment proxy
      if (mediaChild.attrs?.type === 'file' && mediaChild.attrs?.id) {
        return {
          type: 'image',
          attrs: {
            src: `jira-media://${mediaChild.attrs.id}`,
            alt: mediaChild.attrs.alt || mediaChild.attrs.__fileName || 'Jira attachment',
            'data-media-id': mediaChild.attrs.id,
            width: msWidth,
          },
        };
      }
    }
    return null;
  }

  // mediaGroup → array of images (used inside table cells)
  if (node.type === 'mediaGroup') {
    const images = (node.content || [])
      .filter(child => child.type === 'media' && child.attrs?.type === 'file' && child.attrs?.id)
      .map(child => ({
        type: 'image',
        attrs: {
          src: `jira-media://${child.attrs.id}`,
          alt: child.attrs.alt || child.attrs.__fileName || 'Jira attachment',
          'data-media-id': child.attrs.id,
        },
      }));
    return images.length > 0 ? images : null;
  }

  // ADF mention → TipTap mention
  if (node.type === 'mention') {
    return {
      type: 'mention',
      attrs: {
        id: node.attrs?.id || '',
        label: (node.attrs?.text || '').replace(/^@/, ''),
      },
    };
  }

  // ADF inlineCard (smart link) → the editor's inlineCard node, which draws
  // a work item's address as its card (editors/smartLink/SmartLink.jsx) and
  // any other address as a plain link. A card that carries only resolved
  // data (no url) keeps that data's url; with neither it has nothing to show.
  if (node.type === 'inlineCard') {
    const url = node.attrs?.url || node.attrs?.data?.url || '';
    return url ? { type: 'inlineCard', attrs: { url } } : null;
  }

  // ADF emoji → text
  if (node.type === 'emoji') {
    return {
      type: 'text',
      text: node.attrs?.text || node.attrs?.shortName || '',
    };
  }

  // ADF status lozenge → text
  if (node.type === 'status') {
    return {
      type: 'text',
      text: node.attrs?.text || '',
    };
  }

  // ADF date → text
  if (node.type === 'date') {
    return {
      type: 'text',
      text: node.attrs?.timestamp ? formatDate(Number(node.attrs.timestamp), 'date') : '',
    };
  }

  const type = ADF_TO_TIPTAP_NODES[node.type] || node.type;
  const result = { type };

  if (node.attrs) {
    result.attrs = { ...node.attrs };
  }

  if (node.text !== undefined) {
    result.text = node.text;
  }

  if (node.marks) {
    result.marks = node.marks.map(convertMarkAdfToTipTap).filter(Boolean);
  }

  if (node.content) {
    result.content = node.content.flatMap(child => {
      const converted = convertNodeAdfToTipTap(child);
      if (!converted) return [];
      return Array.isArray(converted) ? converted : [converted];
    });
  }

  // Fallback: if the resolved type is unknown to TipTap, unwrap or convert
  if (!KNOWN_TIPTAP_NODES.has(result.type)) {
    // If it has children, unwrap them (return children directly as an array)
    if (result.content && result.content.length > 0) {
      // Wrap children in a paragraph if this was a block-level node
      return { type: 'paragraph', content: result.content };
    }
    // If it has text, convert to text node
    if (result.text) {
      return { type: 'text', text: result.text, marks: result.marks };
    }
    // Drop nodes with no content and no text
    return null;
  }

  return result;
}

function convertNodeTipTapToAdf(node) {
  if (!node) return node;

  // TipTap table → ADF table
  if (node.type === 'table') {
    return {
      type: 'table',
      attrs: { isNumberColumnEnabled: false, layout: 'default' },
      content: (node.content || []).map(convertNodeTipTapToAdf).filter(Boolean),
    };
  }

  if (node.type === 'tableRow') {
    return {
      type: 'tableRow',
      content: (node.content || []).map(convertNodeTipTapToAdf).filter(Boolean),
    };
  }

  if (node.type === 'tableHeader') {
    const attrs = {};
    if (node.attrs?.colspan && node.attrs.colspan > 1) attrs.colspan = node.attrs.colspan;
    if (node.attrs?.rowspan && node.attrs.rowspan > 1) attrs.rowspan = node.attrs.rowspan;
    if (node.attrs?.colwidth) attrs.colwidth = node.attrs.colwidth;
    if (node.attrs?.background) attrs.background = node.attrs.background;
    return {
      type: 'tableHeader',
      attrs: Object.keys(attrs).length ? attrs : undefined,
      content: (node.content || []).map(convertNodeTipTapToAdf).filter(Boolean),
    };
  }

  if (node.type === 'tableCell') {
    const attrs = {};
    if (node.attrs?.colspan && node.attrs.colspan > 1) attrs.colspan = node.attrs.colspan;
    if (node.attrs?.rowspan && node.attrs.rowspan > 1) attrs.rowspan = node.attrs.rowspan;
    if (node.attrs?.colwidth) attrs.colwidth = node.attrs.colwidth;
    if (node.attrs?.background) attrs.background = node.attrs.background;
    return {
      type: 'tableCell',
      attrs: Object.keys(attrs).length ? attrs : undefined,
      content: (node.content || []).map(convertNodeTipTapToAdf).filter(Boolean),
    };
  }

  // image → mediaSingle wrapping media
  if (node.type === 'image') {
    const src = node.attrs?.src || '';
    const imgWidth = node.attrs?.width || null;
    const msAttrs = { layout: 'center' };
    if (imgWidth) msAttrs.width = imgWidth;
    // Preserve internal Jira media references
    if (src.startsWith('jira-media://')) {
      const mediaId = src.replace('jira-media://', '');
      return {
        type: 'mediaSingle',
        attrs: msAttrs,
        content: [{
          type: 'media',
          attrs: { type: 'file', id: mediaId, collection: '' },
        }],
      };
    }
    // If we have a Jira media UUID, use type: 'file' so Jira can render the preview
    const mediaId = node.attrs?.['data-media-id'];
    if (mediaId) {
      return {
        type: 'mediaSingle',
        attrs: msAttrs,
        content: [{
          type: 'media',
          attrs: { type: 'file', id: mediaId, collection: '' },
        }],
      };
    }
    return {
      type: 'mediaSingle',
      attrs: msAttrs,
      content: [{
        type: 'media',
        attrs: { type: 'external', url: src },
      }],
    };
  }

  // TipTap inlineCard → ADF inlineCard: the url, and nothing the editor
  // added on the way.
  if (node.type === 'inlineCard') {
    return { type: 'inlineCard', attrs: { url: node.attrs?.url || '' } };
  }

  // TipTap mention → ADF mention (Jira will send notifications)
  if (node.type === 'mention') {
    return {
      type: 'mention',
      attrs: {
        id: node.attrs?.id || '',
        text: `@${node.attrs?.label || ''}`,
        accessLevel: '',
      },
    };
  }

  const type = TIPTAP_TO_ADF_NODES[node.type] || node.type;
  const result = { type };

  if (node.attrs) {
    result.attrs = { ...node.attrs };
  }

  if (node.text !== undefined) {
    result.text = node.text;
  }

  if (node.marks) {
    result.marks = node.marks.map(convertMarkTipTapToAdf);
  }

  if (node.content) {
    result.content = node.content.map(convertNodeTipTapToAdf).filter(Boolean);
  }

  return result;
}

// Parse Jira wiki markup inline content into TipTap inline nodes
function parseWikiInline(text) {
  if (!text) return [];
  const nodes = [];
  // Regex to match inline wiki patterns in order
  // [~accountid:xxx] mentions, {color:xxx}...{color}, *bold*, !image!, [text|url], [url]
  const pattern = /(\[~accountid:([^\]]+)\])|(\{color:([^}]+)\}([\s\S]*?)\{color\})|(\*([^*\n]+)\*)|(!([^|!\n]+?)(?:\|([^!\n]*))??!)|(\[([^\]\n|]+)\|([^\]\n]+)\])|(\[([^\]\n]+)\])/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    // Add plain text before this match
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }

    if (match[1]) {
      // Mention: [~accountid:xxx]
      nodes.push({
        type: 'mention',
        attrs: { id: match[2], label: match[2].split(':').pop() || match[2] },
      });
    } else if (match[3]) {
      // Color: {color:xxx}text{color}
      const colorContent = parseWikiInline(match[5]);
      for (const n of colorContent) {
        if (n.type === 'text') {
          nodes.push({ ...n, marks: [...(n.marks || []), { type: 'textColor', attrs: { color: match[4] } }] });
        } else {
          nodes.push(n);
        }
      }
    } else if (match[6]) {
      // Bold: *text*
      nodes.push({ type: 'text', text: match[7], marks: [{ type: 'bold' }] });
    } else if (match[8]) {
      // Inline image: !filename|params! — output as placeholder text (block images handled separately)
      nodes.push({ type: 'text', text: `[${match[9]}]` });
    } else if (match[11]) {
      // Link with text: [text|url]
      nodes.push({
        type: 'text',
        text: match[12],
        marks: [{ type: 'link', attrs: { href: match[13], target: '_blank' } }],
      });
    } else if (match[14]) {
      // Bare link: [url] — but skip [^filename] attachment refs (handled at block level)
      if (match[15].startsWith('^')) {
        nodes.push({ type: 'text', text: match[15].slice(1) });
      } else {
        nodes.push({
          type: 'text',
          text: match[15],
          marks: [{ type: 'link', attrs: { href: match[15], target: '_blank' } }],
        });
      }
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push({ type: 'text', text: text.slice(lastIndex) });
  }

  return nodes;
}

// Convert Jira wiki markup string to TipTap doc
function wikiMarkupToTipTap(wiki) {
  if (!wiki) return EMPTY_DOC;
  const content = [];
  // Split into blocks by double newline
  const blocks = wiki.split(/\n\n/);

  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;

    // Horizontal rule
    if (/^-{4,}$/.test(trimmed)) {
      content.push({ type: 'horizontalRule' });
      continue;
    }

    // Heading: h1. through h6. — single-line block where the prefix is the
    // first thing. Without /s the regex naturally won't span newlines, so
    // multi-line blocks fall through to the paragraph branch unchanged.
    const headingMatch = trimmed.match(/^h([1-6])\.\s+(.+)$/);
    if (headingMatch) {
      const level = parseInt(headingMatch[1], 10);
      content.push({
        type: 'heading',
        attrs: { level },
        content: parseWikiInline(headingMatch[2]),
      });
      continue;
    }

    // Blockquote: bq. prefix on a single-line block.
    const bqMatch = trimmed.match(/^bq\.\s+(.+)$/);
    if (bqMatch) {
      content.push({
        type: 'blockquote',
        content: [{
          type: 'paragraph',
          content: parseWikiInline(bqMatch[1]),
        }],
      });
      continue;
    }

    // Image on its own line — block-level image node
    if (/^!([^|!\n]+?)(?:\|([^!\n]*))?!$/.test(trimmed)) {
      const imgMatch = trimmed.match(/^!([^|!\n]+?)(?:\|([^!\n]*))?!$/);
      const filename = imgMatch[1];
      const params = imgMatch[2] || '';
      const widthMatch = params.match(/width=([^,%]+)/);
      content.push({
        type: 'image',
        attrs: {
          src: `wiki-image://${filename}`,
          alt: filename,
          width: widthMatch ? parseFloat(widthMatch[1]) : null,
          'data-wiki-filename': filename,
        },
      });
      continue;
    }

    // Wiki table — consecutive lines starting with | or ||
    const lines = trimmed.split('\n');
    if (lines.length > 0 && lines.every(l => l.trimStart().startsWith('|'))) {
      const tableRows = [];
      for (const line of lines) {
        const tl = line.trim();
        const isHeader = tl.startsWith('||');
        const sep = isHeader ? '||' : '|';
        const parts = tl.split(sep);
        if (parts[0] === '') parts.shift();
        if (parts.length > 0 && parts[parts.length - 1].trim() === '') parts.pop();

        const rowContent = parts.map(cellText => {
          const ct = cellText.trim();
          const cellContent = [];
          // Extract [^filename] attachment references as image nodes
          const attRe = /\[\^([^\]]+)\]/g;
          let lastIdx = 0;
          let attMatch;
          const textParts = [];
          while ((attMatch = attRe.exec(ct)) !== null) {
            if (attMatch.index > lastIdx) textParts.push(ct.slice(lastIdx, attMatch.index));
            cellContent.push({
              type: 'image',
              attrs: { src: `wiki-image://${attMatch[1]}`, alt: attMatch[1] },
            });
            lastIdx = attMatch.index + attMatch[0].length;
          }
          if (lastIdx < ct.length) textParts.push(ct.slice(lastIdx));
          const remaining = textParts.join('').trim();
          if (remaining) {
            cellContent.unshift({ type: 'paragraph', content: parseWikiInline(remaining) });
          }
          if (cellContent.length === 0) {
            cellContent.push({ type: 'paragraph', content: [] });
          }
          return {
            type: isHeader ? 'tableHeader' : 'tableCell',
            content: cellContent,
          };
        });

        if (rowContent.length > 0) {
          tableRows.push({ type: 'tableRow', content: rowContent });
        }
      }
      if (tableRows.length > 0) {
        content.push({ type: 'table', content: tableRows });
        continue;
      }
    }

    // Regular paragraph — parse inline content
    // Handle single newlines within a block as line breaks
    const inlineContent = [];
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) inlineContent.push({ type: 'hardBreak' });
      const parsed = parseWikiInline(lines[i]);
      inlineContent.push(...parsed);
    }

    if (inlineContent.length > 0) {
      content.push({ type: 'paragraph', content: inlineContent });
    }
  }

  if (content.length === 0) {
    return EMPTY_DOC;
  }

  return { type: 'doc', content };
}

// Promote legacy Jira wiki-markup paragraphs ("h2. Heading", "bq. Quote") to
// proper ADF heading/blockquote nodes. Only matches a paragraph whose ENTIRE
// content is a single text node, so mixed-formatting paragraphs are left alone
// and false positives on legitimate "h2." prose stay rare.
//
// Returns the ADF unchanged (same reference) when there's nothing to promote,
// so callers can treat it as a no-op when authors use proper ADF.
//
// Use only in read-mode display paths. Edit mode should keep the raw wiki text
// visible so users can edit and save it back, otherwise we'd silently rewrite
// the stored ADF on every save.
export function preprocessAdfWikiMarkup(adfDoc) {
  if (!adfDoc || typeof adfDoc !== 'object' || adfDoc.type !== 'doc' || !Array.isArray(adfDoc.content)) {
    return adfDoc;
  }
  let mutated = false;
  const newContent = adfDoc.content.map(node => {
    if (node.type !== 'paragraph' || !Array.isArray(node.content) || node.content.length !== 1) return node;
    const child = node.content[0];
    if (child.type !== 'text' || typeof child.text !== 'string') return node;

    const headingMatch = child.text.match(/^h([1-6])\.\s+(.*)$/);
    if (headingMatch) {
      mutated = true;
      const level = parseInt(headingMatch[1], 10);
      const restText = headingMatch[2];
      return {
        type: 'heading',
        attrs: { level },
        content: [{ type: 'text', text: restText, ...(child.marks ? { marks: child.marks } : {}) }],
      };
    }
    const bqMatch = child.text.match(/^bq\.\s+(.*)$/);
    if (bqMatch) {
      mutated = true;
      return {
        type: 'blockquote',
        content: [{
          type: 'paragraph',
          content: [{ type: 'text', text: bqMatch[1], ...(child.marks ? { marks: child.marks } : {}) }],
        }],
      };
    }
    return node;
  });
  return mutated ? { ...adfDoc, content: newContent } : adfDoc;
}

export function adfToTipTap(adfDoc) {
  if (!adfDoc) return EMPTY_DOC;

  // Handle string values — could be wiki markup or serialized ADF JSON
  if (typeof adfDoc === 'string') {
    // Extract ADF from {adf:display=block}...{adf} wiki markup wrapper
    const adfWrapMatch = adfDoc.match(/\{adf(?::display=\w+)?\}\s*([\s\S]*?)\s*\{adf\}/);
    if (adfWrapMatch) {
      try {
        const inner = JSON.parse(adfWrapMatch[1]);
        if (inner?.type === 'doc') return convertNodeAdfToTipTap(inner);
        // If the inner JSON is a single node (e.g. table), wrap in doc
        if (inner?.type) return convertNodeAdfToTipTap({ type: 'doc', content: [inner] });
      } catch { /* not valid JSON, fall through to wiki markup */ }
    }
    if (adfDoc.startsWith('{"type":"doc"')) {
      try {
        const parsed = JSON.parse(adfDoc);
        if (parsed?.type === 'doc') return convertNodeAdfToTipTap(parsed);
      } catch { /* not valid JSON, fall through to wiki markup */ }
    }
    return wikiMarkupToTipTap(adfDoc);
  }

  if (adfDoc.type !== 'doc') return EMPTY_DOC;

  return convertNodeAdfToTipTap(adfDoc);
}

export function tipTapToAdf(tipTapDoc) {
  if (!tipTapDoc) return { type: 'doc', version: 1, content: [{ type: 'paragraph', content: [] }] };

  const adf = convertNodeTipTapToAdf(tipTapDoc);
  adf.version = 1;
  return adf;
}
